import React, { useEffect } from 'react';
import Header from '../layout/Header';
import Footer from '../layout/Footer';

const DataDeletionPage = () => {
  useEffect(() => {
    document.title = 'User Data Deletion · AutoPostr';
  }, []);

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white">
      <Header />
      <main className="pt-32 pb-24 px-6">
        <article className="max-w-3xl mx-auto" data-testid="data-deletion-content">
          <header className="mb-10">
            <p className="text-sm text-white/50 uppercase tracking-wider mb-3">Legal</p>
            <h1 className="text-3xl md:text-5xl font-bold mb-3">AutoPostr – User Data Deletion</h1>
            <p className="text-white/60 text-sm">
              Effective date: <span className="text-white/80">1 September 2026</span>
            </p>
          </header>

          <section>
            <p className="text-white/70 leading-relaxed mb-6">
              AutoPostr respects your privacy and provides a clear process for requesting
              deletion of personal information and data associated with your AutoPostr
              account. AutoPostr is a social-media management and content publishing
              platform that allows you to connect Facebook, Instagram and Threads accounts
              through official Meta APIs.
            </p>

            <h2 className="text-xl md:text-2xl font-bold text-white mt-10 mb-4">
              How to request data deletion
            </h2>
            <p className="text-white/70 leading-relaxed mb-4">
              To request deletion of your AutoPostr account and associated personal data,
              please email us:
            </p>

            <div className="bg-[#141414] border border-white/10 rounded-2xl p-6 mb-6">
              <p className="text-white font-semibold mb-3">Email us at:</p>
              <a
                href="mailto:support@autopostr.co.uk?subject=AutoPostr%20Data%20Deletion%20Request"
                data-testid="data-deletion-email-link"
                className="inline-block px-5 py-2.5 rounded-full bg-emerald-400 hover:bg-emerald-500 text-black font-semibold transition-colors"
              >
                support@autopostr.co.uk
              </a>
              <p className="text-white/60 text-sm mt-4">
                Please include:
              </p>
              <ul className="list-disc pl-6 space-y-1.5 mt-2 text-white/70 text-sm">
                <li>The email address associated with your AutoPostr account</li>
                <li>A clear request to delete your AutoPostr account and associated personal data</li>
              </ul>
              <p className="text-white/50 text-xs mt-4">
                Please do <span className="text-white/80">not</span> include passwords,
                access tokens, payment-card information or other sensitive information that
                is not required to process your request.
              </p>
            </div>

            <h2 className="text-xl md:text-2xl font-bold text-white mt-10 mb-4">
              Social-media connections
            </h2>
            <p className="text-white/70 leading-relaxed mb-4">
              You can disconnect connected Facebook, Instagram and Threads accounts from
              AutoPostr where the functionality is available in your account settings.
              You can also request deletion of data associated with your connected
              social-media accounts by contacting us at{' '}
              <a href="mailto:support@autopostr.co.uk" className="text-emerald-400 hover:text-emerald-300 underline">
                support@autopostr.co.uk
              </a>.
            </p>

            <h2 className="text-xl md:text-2xl font-bold text-white mt-10 mb-4">
              What happens after a request
            </h2>
            <p className="text-white/70 leading-relaxed mb-4">
              After receiving your request, AutoPostr will review it and take appropriate
              steps to delete personal information that we are no longer required or
              permitted to retain.
            </p>
            <p className="text-white/70 leading-relaxed mb-4">
              Some information may need to be retained where required by law, for
              legitimate legal purposes, fraud prevention, security, accounting or dispute
              resolution. AutoPostr cannot guarantee immediate deletion in every case, and
              backups may take additional time to be cycled out in the ordinary course of
              operations.
            </p>

            <h2 className="text-xl md:text-2xl font-bold text-white mt-10 mb-4">
              Meta data
            </h2>
            <p className="text-white/70 leading-relaxed mb-4">
              When you authorise AutoPostr to connect to Facebook, Instagram or Threads,
              AutoPostr may receive certain information from Meta services (for example,
              basic profile information and access tokens needed to publish or manage
              content on your behalf). You may request deletion of AutoPostr&rsquo;s stored
              data associated with these connections at any time by contacting us.
            </p>
            <p className="text-white/70 leading-relaxed mb-4">
              AutoPostr cannot delete information directly from Meta&rsquo;s own systems.
              To manage data held by Meta, you may also need to use the settings and
              controls available directly within Facebook, Instagram or Threads (for
              example, &ldquo;Business Integrations&rdquo; in your Meta account settings),
              or contact Meta directly.
            </p>

            <h2 className="text-xl md:text-2xl font-bold text-white mt-10 mb-4">
              Contact
            </h2>
            <p className="text-white/70 leading-relaxed mb-4">
              For any questions about deletion of your data, please contact us at{' '}
              <a
                href="mailto:support@autopostr.co.uk"
                className="text-emerald-400 hover:text-emerald-300 underline"
              >
                support@autopostr.co.uk
              </a>.
            </p>

            <h2 className="text-xl md:text-2xl font-bold text-white mt-10 mb-4">
              Related policies
            </h2>
            <p className="text-white/70 leading-relaxed mb-4">
              For details of what information we collect and how we use it, please see our{' '}
              <a href="/privacy-policy" className="text-emerald-400 hover:text-emerald-300 underline">
                Privacy Policy
              </a>. For the terms that govern your use of AutoPostr, please see our{' '}
              <a href="/terms" className="text-emerald-400 hover:text-emerald-300 underline">
                Terms of Service
              </a>.
            </p>

            <hr className="border-white/10 my-10" />
            <p className="text-white/40 text-sm">
              Questions? Contact us at{' '}
              <a href="mailto:support@autopostr.co.uk" className="text-emerald-400 hover:text-emerald-300 underline">
                support@autopostr.co.uk
              </a>.
            </p>
          </section>
        </article>
      </main>
      <Footer />
    </div>
  );
};

export default DataDeletionPage;
